import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  User as UserIcon, Building2, MapPin, Image as ImageIcon, Percent, BadgeEuro,
  Bell, Globe2, Users, X, Send, Check, Loader2, Save, FileBadge, AlertTriangle,
  Mail, Trash2, ChevronRight, Crown, Receipt, PieChart, Shield, FileDown,
  Sun, Snowflake, Calendar as CalendarIcon, MapPinned, TrendingDown, TrendingUp,
} from "lucide-react";
import api, { formatApiError, API } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useOutlets } from "../components/OutletContext";
import { Button, Card, Input, Label, Spinner, Badge, Select } from "../components/ui/Primitives";
import { formatEUR as fmtEur } from "../utils/format";

// Phase 25 — 6 team roles
const ROLES_INFO = {
  owner:        { label_de: "Inhaber (Zentrale)", label_en: "Owner (HQ)",     tone: "brand",   desc_de: "Alle Outlets, Billing, Benutzerverwaltung." },
  gm:           { label_de: "General Manager",     label_en: "General Manager", tone: "purple", desc_de: "Zugewiesene Outlets, voller Zugriff inkl. Costs und Rooms." },
  fnb_manager:  { label_de: "F&B Manager",         label_en: "F&B Manager",     tone: "cyan",   desc_de: "Küche/Bar/Menüs/Waste bearbeiten — keine Costs/Rooms/Settings." },
  kitchen_chef: { label_de: "Küchenchef",          label_en: "Head Chef",       tone: "ok",     desc_de: "Küche und Menüs bearbeiten, Waste nur Küche." },
  bar_manager:  { label_de: "Barmanager",          label_en: "Bar Manager",     tone: "warn",   desc_de: "Bar bearbeiten, Menüs/Rezepte lesen, Waste nur Bar." },
  service:      { label_de: "Service",             label_en: "Service Staff",   tone: "default", desc_de: "Alles lesen, keine Preise sichtbar." },
};
const ROLE_KEYS = ["owner", "gm", "fnb_manager", "kitchen_chef", "bar_manager", "service"];

// -------- Auto-save hook --------
function useAutoSave(initial, onSave, delay = 600) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);
  const skipFirst = useRef(true);

  useEffect(() => { setValue(initial); skipFirst.current = true; }, [initial]);

  const patch = (delta) => {
    setValue((v) => ({ ...v, ...delta }));
  };

  useEffect(() => {
    if (skipFirst.current) { skipFirst.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await onSave(value);
        setSavedAt(Date.now());
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSaving(false);
      }
    }, delay);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return { value, patch, saving, savedAt };
}

function SaveIndicator({ saving, savedAt }) {
  const { t } = useTranslation();
  if (saving) return (
    <span className="text-[11px] text-ink-muted inline-flex items-center gap-1.5" data-testid="save-indicator-saving">
      <Loader2 size={11} className="animate-spin" /> {t("settings.saving")}
    </span>
  );
  if (savedAt && Date.now() - savedAt < 4000) return (
    <span className="text-[11px] text-ok inline-flex items-center gap-1.5" data-testid="save-indicator-saved">
      <Check size={11} /> {t("settings.saved")}
    </span>
  );
  return null;
}

// -------- Section header --------
function Section({ icon: Icon, title, sub, children, testId, right }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand/10 border border-brand/20 flex items-center justify-center text-brand shrink-0">
            <Icon size={16} strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-base font-semibold text-ink-primary tracking-tight">{title}</div>
            {sub && <div className="text-xs text-ink-muted mt-0.5 max-w-md">{sub}</div>}
          </div>
        </div>
        <div>{right}</div>
      </div>
      <div className="space-y-3">{children}</div>
    </Card>
  );
}

// =================== Operation Type (Feature 4) ===================
const OPERATION_TYPES_LIST = ["jahres", "sommer", "winter", "gemischt"];
const MONTH_LABELS_DE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function OperationCalendar({ activeMonths, onToggle, currentMonth }) {
  return (
    <div className="grid grid-cols-6 sm:grid-cols-12 gap-1" data-testid="operation-calendar">
      {MONTH_LABELS_DE.map((lbl, i) => {
        const m = i + 1;
        const active = activeMonths.includes(m);
        const isCurrent = m === currentMonth;
        return (
          <button
            key={m}
            onClick={() => onToggle(m)}
            className={`px-2 py-2 rounded-md text-xs font-medium border transition-colors ${
              active
                ? "bg-ok/20 border-ok/40 text-ok"
                : "bg-bg-void border-white/5 text-ink-muted hover:border-white/15"
            } ${isCurrent ? "ring-2 ring-brand/60" : ""}`}
            data-testid={`op-month-${m}`}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

function OperationProfileSection() {
  const { t } = useTranslation();
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/insights/operation-profile");
        setOp(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleMonth = (m) => {
    setOp((o) => {
      const months = o.active_months.includes(m)
        ? o.active_months.filter((x) => x !== m)
        : [...o.active_months, m].sort((a, b) => a - b);
      return { ...o, active_months: months };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        operation_type: op.operation_type,
        active_months: op.active_months,
        season_label: op.season_label,
        target_revenue_eur: Number(op.target_revenue_eur) || 0,
      };
      const { data } = await api.put("/insights/operation-profile", payload);
      setOp(data);
      toast.success(t("settings.operation.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Apply preset on type change
  const onTypeChange = (type) => {
    let months = op.active_months;
    if (type === "jahres") months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    else if (type === "sommer") months = [5, 6, 7, 8, 9];
    else if (type === "winter") months = [12, 1, 2, 3, 4];
    // gemischt → keep current selection
    setOp({ ...op, operation_type: type, active_months: months });
  };

  if (loading || !op) return <Card className="p-5"><Spinner size={16} /></Card>;

  return (
    <Section icon={CalendarIcon} title={t("settings.operation.title")} sub={t("settings.operation.sub")} testId="settings-section-operation">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("settings.operation.type")}</Label>
            <Select value={op.operation_type} onChange={(e) => onTypeChange(e.target.value)} data-testid="op-type">
              {OPERATION_TYPES_LIST.map((k) => <option key={k} value={k}>{t(`settings.operation.type_${k}`)}</option>)}
            </Select>
          </div>
          <div>
            <Label>{t("settings.operation.season_label")}</Label>
            <Input value={op.season_label || ""} onChange={(e) => setOp({ ...op, season_label: e.target.value })} placeholder="Sommer 2026" />
          </div>
          <div>
            <Label>{t("settings.operation.target_revenue")}</Label>
            <Input type="number" value={op.target_revenue_eur || 0} onChange={(e) => setOp({ ...op, target_revenue_eur: e.target.value })} data-testid="op-target-revenue" />
          </div>
        </div>

        <div>
          <Label>{t("settings.operation.active_months")}</Label>
          <OperationCalendar activeMonths={op.active_months} onToggle={toggleMonth} currentMonth={op.current_month} />
          <p className="text-xs text-ink-muted mt-2">{t("settings.operation.calendar_hint")}</p>
        </div>

        {op.in_season && op.total_season_months > 0 && (
          <Card className="p-3 bg-brand/10 border-brand/30" data-testid="season-progress">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="text-ink-primary">{t("settings.operation.progress_label", { idx: op.current_month_index, total: op.total_season_months })}</span>
              <span className="num text-brand">{op.progress_pct}%</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-brand transition-all" style={{ width: `${op.progress_pct}%` }} />
            </div>
          </Card>
        )}

        <div>
          <Button onClick={save} disabled={saving} data-testid="op-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t("settings.operation.save")}
          </Button>
        </div>
      </div>
    </Section>
  );
}


// =================== KV Benchmarks (Feature 5) ===================
function KVBenchmarkSection() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [rent, setRent] = useState(null);
  const [sqm, setSqm] = useState(200);
  const [seasonal, setSeasonal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/insights/kv-benchmark?seasonal=${seasonal}`);
        setData(r.data);
      } catch { /* ignore */ }
    })();
  }, [seasonal]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Try to read user's rent from business cost profile + sqm from local input
      try {
        const biz = await api.get("/cost-profile/business");
        const monthly = Number(biz.data.rent || 0);
        if (monthly > 0) {
          const r = await api.get(`/insights/rent-benchmark?monthly_rent=${monthly}&sqm=${sqm}`);
          if (!cancelled) setRent(r.data);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [sqm]);

  if (!data) return null;
  const rates = data.rates_eur_month || {};

  return (
    <Section icon={MapPinned} title={t("settings.kv.title")} sub={t("settings.kv.sub", { state: data.state_name })} testId="settings-section-kv">
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Badge tone="brand">{data.state_name}</Badge>
          <label className="text-xs text-ink-muted flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={seasonal} onChange={(e) => setSeasonal(e.target.checked)} data-testid="kv-seasonal-toggle" />
            {t("settings.kv.seasonal_toggle")}
          </label>
          {seasonal && <span className="text-xs text-warn">+{data.premium_pct}%</span>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="kv-rates">
          {Object.entries(rates).map(([role, eur]) => (
            <div key={role} className="bg-bg-void rounded-md px-3 py-2 border border-white/5">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t(`settings.kv.role_${role}`, role)}</div>
              <div className="num text-base text-ink-primary">{Number(eur).toLocaleString("de-AT")} €</div>
            </div>
          ))}
        </div>

        {rent && (
          <div className={`rounded-md p-3 border ${rent.verdict === "above" ? "border-warn/30 bg-warn/5" : rent.verdict === "below" ? "border-ok/30 bg-ok/5" : "border-white/5 bg-bg-void"}`} data-testid="kv-rent-benchmark">
            <div className="flex items-center gap-2 text-sm">
              {rent.verdict === "above" ? <TrendingUp size={14} className="text-warn" /> : rent.verdict === "below" ? <TrendingDown size={14} className="text-ok" /> : null}
              <span className="text-ink-primary">
                {t("settings.kv.rent_msg", {
                  delta: Math.abs(rent.delta_pct),
                  direction: rent.delta_pct >= 0 ? t("settings.kv.above") : t("settings.kv.below"),
                  state: rent.state_name,
                })}
              </span>
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{t("settings.kv.rent_avg", { avg: rent.regional_avg_per_sqm })}</div>
          </div>
        )}

        <div className="flex items-end gap-2 text-xs">
          <div>
            <Label className="text-[10px]">{t("settings.kv.sqm")}</Label>
            <Input type="number" value={sqm} onChange={(e) => setSqm(Number(e.target.value) || 200)} className="w-24" data-testid="kv-sqm" />
          </div>
        </div>
      </div>
    </Section>
  );
}


// =================== Legal (Feature B) ===================
function LegalSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/legal/status");
      setStatus(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const manualAccept = async () => {
    setAccepting(true);
    try {
      await api.post("/legal/accept", {});
      toast.success(t("settings.legal.accepted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAccepting(false); }
  };

  if (loading || !status) return <Card className="p-5"><Spinner size={16} /></Card>;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-AT") : "—";

  return (
    <Section icon={Shield} title={t("settings.legal.title")} sub={t("settings.legal.sub")} testId="settings-section-legal">
      <div className="space-y-4">
        {status.accepted ? (
          <Card className="p-3 border-ok/30 bg-ok/5" data-testid="legal-accepted-banner">
            <div className="flex items-center gap-2 text-sm">
              <Check size={14} className="text-ok" />
              <span>{t("settings.legal.accepted_on", { date: fmtDate(status.accepted_at) })}</span>
              <Badge tone="ok">{t("settings.legal.active")}</Badge>
            </div>
            {status.accepted_via && <div className="text-[11px] text-ink-muted mt-1">{t("settings.legal.via")}: {status.accepted_via}</div>}
          </Card>
        ) : (
          <Card className="p-3 border-warn/30 bg-warn/5" data-testid="legal-not-accepted-banner">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle size={14} className="text-warn" />
              <span>{t("settings.legal.not_accepted")}</span>
            </div>
            <Button size="sm" onClick={manualAccept} disabled={accepting} className="mt-2" data-testid="legal-manual-accept">
              {accepting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {t("settings.legal.accept_now")}
            </Button>
          </Card>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">AVV</div>
            <h3 className="font-semibold text-ink-primary mb-1">{t("settings.legal.avv_title")}</h3>
            <div className="text-xs text-ink-muted mb-3">v{status.avv_version}</div>
            <Button variant="secondary" size="sm" onClick={() => window.open(`${API}/legal/avv/pdf`, "_blank")} data-testid="legal-avv-pdf">
              <FileDown size={12} /> PDF herunterladen
            </Button>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">DPA</div>
            <h3 className="font-semibold text-ink-primary mb-1">{t("settings.legal.dpa_title")}</h3>
            <div className="text-xs text-ink-muted mb-3">v{status.dpa_version}</div>
            <Button variant="secondary" size="sm" onClick={() => window.open(`${API}/legal/dpa/pdf`, "_blank")} data-testid="legal-dpa-pdf">
              <FileDown size={12} /> PDF herunterladen
            </Button>
          </Card>
        </div>
      </div>
    </Section>
  );
}


// =================== Betriebskosten (Feature 4) ===================
const fmtNum = (v, d = 2) => `${Number(v || 0).toFixed(d).replace(".", ",")}`;

function BetriebskostenDonut({ data }) {
  // data: { rent, staff_cost, energy, other } in € (monthly)
  const segs = [
    { key: "rent", color: "#a78bfa", value: data.rent || 0 },
    { key: "staff_cost", color: "#60a5fa", value: data.staff_cost || 0 },
    { key: "energy", color: "#34d399", value: data.energy || 0 },
    { key: "other", color: "#f59e0b", value: data.other || 0 },
  ];
  const total = segs.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return <div className="text-xs text-ink-muted text-center py-6">—</div>;
  const cx = 60, cy = 60, r = 48, stroke = 12;
  const C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <circle cx={cx} cy={cy} r={r} fill="transparent" stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} />
      {segs.map((s, i) => {
        if (!s.value) return null;
        const frac = s.value / total;
        const dash = C * frac;
        const offset = -(acc * C);
        acc += frac;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="transparent"
            stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${C - dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
    </svg>
  );
}

function BetriebskostenSection() {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cost-profile/business");
        setDoc(data);
      } catch (e) { /* not super-critical */ }
      finally { setLoading(false); }
    })();
  }, []);

  const patch = (delta) => setDoc((d) => ({ ...d, ...delta }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rent: Number(doc.rent) || 0,
        staff_cost: Number(doc.staff_cost) || 0,
        staff_fte: Number(doc.staff_fte) || 0,
        energy: Number(doc.energy) || 0,
        other: Number(doc.other) || 0,
        monthly_covers: Number(doc.monthly_covers) || 0,
        mode: doc.mode || "restaurant",
        notes: doc.notes || "",
      };
      const { data } = await api.put("/cost-profile/business", payload);
      setDoc(data);
      toast.success(t("settings.betriebskosten.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <Card className="p-5"><Spinner size={16} /></Card>;
  if (!doc) return null;

  return (
    <Section icon={Receipt} title={t("settings.betriebskosten.title")} sub={t("settings.betriebskosten.sub")} testId="settings-section-betriebskosten">
      <div className="grid md:grid-cols-[1fr_auto] gap-6">
        <div className="space-y-3">
          <div>
            <Label>{t("settings.betriebskosten.mode")}</Label>
            <Select value={doc.mode || "restaurant"} onChange={(e) => patch({ mode: e.target.value })} data-testid="bk-mode">
              <option value="restaurant">{t("settings.betriebskosten.mode_restaurant")}</option>
              <option value="bar">{t("settings.betriebskosten.mode_bar")}</option>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("settings.betriebskosten.rent")}</Label>
              <Input type="number" step="0.01" value={doc.rent} onChange={(e) => patch({ rent: e.target.value })} data-testid="bk-rent" />
            </div>
            <div>
              <Label>{t("settings.betriebskosten.staff_cost")}</Label>
              <Input type="number" step="0.01" value={doc.staff_cost} onChange={(e) => patch({ staff_cost: e.target.value })} data-testid="bk-staff-cost" />
            </div>
            <div>
              <Label>{t("settings.betriebskosten.staff_fte")}</Label>
              <Input type="number" step="0.1" value={doc.staff_fte} onChange={(e) => patch({ staff_fte: e.target.value })} data-testid="bk-staff-fte" />
            </div>
            <div>
              <Label>{t("settings.betriebskosten.energy")}</Label>
              <Input type="number" step="0.01" value={doc.energy} onChange={(e) => patch({ energy: e.target.value })} data-testid="bk-energy" />
            </div>
            <div>
              <Label>{t("settings.betriebskosten.other")}</Label>
              <Input type="number" step="0.01" value={doc.other} onChange={(e) => patch({ other: e.target.value })} data-testid="bk-other" />
            </div>
            <div>
              <Label>{doc.mode === "bar" ? t("settings.betriebskosten.drinks_per_month") : t("settings.betriebskosten.covers_per_month")}</Label>
              <Input type="number" value={doc.monthly_covers} onChange={(e) => patch({ monthly_covers: e.target.value })} data-testid="bk-covers" />
            </div>
          </div>
          <div className="pt-2">
            <Button onClick={save} disabled={saving} data-testid="bk-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t("settings.betriebskosten.save")}
            </Button>
          </div>
        </div>

        {/* Visualization */}
        <div className="bg-bg-void/60 rounded-lg p-5 flex flex-col items-center justify-center min-w-[240px]" data-testid="bk-summary">
          <BetriebskostenDonut data={doc} />
          <div className="mt-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("settings.betriebskosten.total_monthly")}</div>
            <div className="num text-xl font-medium text-ink-primary" data-testid="bk-total">{fmtEur(doc.total_monthly)}</div>
          </div>
          <div className="mt-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">
              {doc.mode === "bar" ? t("settings.betriebskosten.cost_per_drink") : t("settings.betriebskosten.cost_per_cover")}
            </div>
            <div className="num text-2xl font-medium text-brand" data-testid="bk-cpc">{fmtEur(doc.cost_per_cover)}</div>
            <div className="text-[10px] text-ink-muted mt-1">{t("settings.betriebskosten.cpc_hint")}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-1.5 text-[10px] w-full">
            {[
              ["rent", "#a78bfa"],
              ["staff_cost", "#60a5fa"],
              ["energy", "#34d399"],
              ["other", "#f59e0b"],
            ].map(([k, c]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                <span className="text-ink-muted">{t(`settings.betriebskosten.${k}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

// =================== Page ===================
export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const { activeId: activeOutletId } = useOutlets();
  const fileRef = useRef(null);

  // Phase 34 — per-outlet settings: fetch effective merged settings from
  // GET /api/settings (returns the active outlet's overrides on top of the
  // user doc). Refetches whenever the user switches outlet in the sidebar.
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setSettings(data);
      } catch (e) {
        if (!cancelled) setSettings(user || {});
      }
    };
    if (user) load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeOutletId]);

  // Initial state from effective settings (per-outlet merged).
  const initial = useMemo(() => {
    const src = settings || user || {};
    return {
      business_name: src.business_name || "",
      business_address: src.business_address || "",
      vat_number: src.vat_number || "",
      target_food_cost: src.target_food_cost ?? 30,
      target_pour_cost: src.target_pour_cost ?? 20,
      target_db1_min: src.target_db1_min ?? "",
      language: src.language || "de",
      notify_low_credits: src.notify_low_credits ?? true,
      notify_demo_expiry: src.notify_demo_expiry ?? true,
      logo_base64: src.logo_base64 || "",
    };
  }, [settings, user]);

  const save = async (val) => {
    const payload = { ...val };
    // Normalize numbers
    if (payload.target_db1_min === "" || payload.target_db1_min === null) {
      delete payload.target_db1_min;
    } else {
      payload.target_db1_min = Number(payload.target_db1_min);
    }
    payload.target_food_cost = Number(payload.target_food_cost);
    payload.target_pour_cost = Number(payload.target_pour_cost);
    const { data } = await api.patch("/settings", payload);
    if (payload.language && payload.language !== i18n.language) {
      i18n.changeLanguage(payload.language);
    }
    // Refresh local effective settings + auth context (language/notifications).
    setSettings(data);
    await refresh();
  };

  const { value, patch, saving, savedAt } = useAutoSave(initial, save);

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 600 * 1024) {
      toast.error(t("settings.logo_too_large"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => patch({ logo_base64: String(reader.result) });
    reader.readAsDataURL(f);
  };

  // -------- Team --------
  const { outlets: allOutlets } = useOutlets();
  const [team, setTeam] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("fnb_manager");
  const [inviteName, setInviteName] = useState("");
  const [inviteOutletIds, setInviteOutletIds] = useState([]);
  const [inviting, setInviting] = useState(false);
  // Phase 27 — dev token UI removed; setup token is sent via Brevo (or printed to backend console fallback)

  const loadTeam = async () => {
    try {
      const { data } = await api.get("/team");
      setTeam(data);
    } catch (e) { /* may 401 right after login race; ignore */ }
  };
  useEffect(() => { if (user) loadTeam(); /* eslint-disable-next-line */ }, [user?.id]);

  const sendInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const { data } = await api.post("/team/invite", {
        email: inviteEmail.trim(),
        role: inviteRole,
        name: inviteName.trim(),
        outlet_ids: inviteOutletIds,
      });
      toast.success(t("settings.team.invite_sent"));
      setInviteEmail(""); setInviteName(""); setInviteOutletIds([]);
      // Phase 27 — dev token never shown in UI; email is sent via Brevo with console fallback
      await loadTeam();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setInviting(false); }
  };

  const removeMember = async (id, email) => {
    if (!window.confirm(t("settings.team.remove_confirm", { email }))) return;
    try {
      await api.delete(`/team/members/${id}`);
      toast.success(t("settings.team.removed"));
      await loadTeam();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeRole = async (id, role, currentOutletIds) => {
    try {
      await api.patch(`/team/members/${id}`, { role, outlet_ids: currentOutletIds || [] });
      toast.success(t("settings.saved_action"));
      await loadTeam();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleMemberOutlet = async (m, outletId) => {
    const cur = new Set(m.team_outlet_ids || []);
    if (cur.has(outletId)) cur.delete(outletId); else cur.add(outletId);
    try {
      await api.patch(`/team/members/${m.id}`, { role: m.team_role || "fnb_manager", outlet_ids: Array.from(cur) });
      await loadTeam();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) return <div className="flex justify-center py-16"><Spinner size={28} /></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">
            {t("nav.settings")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-ink-primary" data-testid="settings-title">
            {t("settings.title")}
          </h1>
          <p className="text-sm text-ink-secondary mt-2 max-w-2xl">{t("settings.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <SaveIndicator saving={saving} savedAt={savedAt} />
        </div>
      </header>

      {/* ============ Business / Profile ============ */}
      <Section icon={Building2} title={t("settings.profile_title")} sub={t("settings.profile_sub")} testId="settings-section-profile">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>{t("settings.business_name")}</Label>
            <Input
              value={value.business_name}
              onChange={(e) => patch({ business_name: e.target.value })}
              placeholder={t("settings.business_name_ph")}
              data-testid="settings-business-name"
            />
          </div>
          <div>
            <Label>{t("settings.vat_number")}</Label>
            <Input
              value={value.vat_number}
              onChange={(e) => patch({ vat_number: e.target.value.toUpperCase() })}
              placeholder="ATU12345678"
              data-testid="settings-vat"
              className="font-mono"
            />
          </div>
        </div>
        <div>
          <Label>{t("settings.address")}</Label>
          <textarea
            value={value.business_address}
            onChange={(e) => patch({ business_address: e.target.value })}
            rows={2}
            data-testid="settings-address"
            placeholder={t("settings.address_ph")}
            className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary focus:border-brand focus:outline-none resize-none"
          />
        </div>

        <div className="flex items-center gap-4 pt-1">
          <div className="w-20 h-20 rounded-lg border border-white/10 bg-bg-void flex items-center justify-center overflow-hidden shrink-0" data-testid="settings-logo-preview">
            {value.logo_base64 ? (
              <img src={value.logo_base64} alt="logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <ImageIcon size={20} className="text-ink-muted" />
            )}
          </div>
          <div className="flex-1">
            <Label>{t("settings.logo")}</Label>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogoFile} className="hidden" data-testid="settings-logo-input" />
              <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()} data-testid="settings-logo-pick">
                <ImageIcon size={14} /> {value.logo_base64 ? t("settings.logo_replace") : t("settings.logo_upload")}
              </Button>
              {value.logo_base64 && (
                <Button size="sm" variant="ghost" onClick={() => patch({ logo_base64: "" })} data-testid="settings-logo-remove">
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{t("settings.logo_hint")}</div>
          </div>
        </div>
      </Section>

      {/* ============ Calculation targets ============ */}
      <Section icon={Percent} title={t("settings.calc_title")} sub={t("settings.calc_sub")} testId="settings-section-calc">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>{t("settings.target_fc")}</Label>
            <div className="relative">
              <Input
                type="number" min="0" max="100" step="0.5"
                value={value.target_food_cost}
                onChange={(e) => patch({ target_food_cost: e.target.value })}
                data-testid="settings-target-fc"
                className="pr-9"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted text-xs">%</span>
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{t("settings.target_fc_hint")}</div>
          </div>
          <div>
            <Label>{t("settings.target_pc")}</Label>
            <div className="relative">
              <Input
                type="number" min="0" max="100" step="0.5"
                value={value.target_pour_cost}
                onChange={(e) => patch({ target_pour_cost: e.target.value })}
                data-testid="settings-target-pc"
                className="pr-9"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted text-xs">%</span>
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{t("settings.target_pc_hint")}</div>
          </div>
          <div>
            <Label>{t("settings.target_db1")}</Label>
            <div className="relative">
              <Input
                type="number" min="0" max="9999" step="0.5"
                value={value.target_db1_min}
                onChange={(e) => patch({ target_db1_min: e.target.value })}
                placeholder={t("settings.target_db1_ph")}
                data-testid="settings-target-db1"
                className="pr-9"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted text-xs">€</span>
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{t("settings.target_db1_hint")}</div>
          </div>
        </div>
      </Section>

      {/* ============ Betriebskosten — Feature 4 ============ */}
      <BetriebskostenSection />

      {/* ============ Operation Type (Phase 17 — Feature 4) ============ */}
      <OperationProfileSection />

      {/* ============ KV Benchmarks (Phase 17 — Feature 5) ============ */}
      <KVBenchmarkSection />

      {/* ============ Legal / DSGVO (Phase 17 — Feature B) ============ */}
      <LegalSection />

      {/* ============ Language & Notifications ============ */}
      <Section icon={Bell} title={t("settings.prefs_title")} sub={t("settings.prefs_sub")} testId="settings-section-prefs">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>{t("settings.language")}</Label>
            <Select
              value={value.language}
              onChange={(e) => patch({ language: e.target.value })}
              data-testid="settings-language"
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </Select>
          </div>
        </div>
        <div className="pt-2 space-y-2">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-notify-low-credits-label">
            <input
              type="checkbox"
              checked={!!value.notify_low_credits}
              onChange={(e) => patch({ notify_low_credits: e.target.checked })}
              className="accent-brand mt-0.5"
              data-testid="settings-notify-low-credits"
            />
            <span>
              <span className="text-sm text-ink-primary">{t("settings.notify_low_credits")}</span>
              <span className="block text-[11px] text-ink-muted">{t("settings.notify_low_credits_sub")}</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-notify-demo-label">
            <input
              type="checkbox"
              checked={!!value.notify_demo_expiry}
              onChange={(e) => patch({ notify_demo_expiry: e.target.checked })}
              className="accent-brand mt-0.5"
              data-testid="settings-notify-demo"
            />
            <span>
              <span className="text-sm text-ink-primary">{t("settings.notify_demo_expiry")}</span>
              <span className="block text-[11px] text-ink-muted">{t("settings.notify_demo_expiry_sub")}</span>
            </span>
          </label>
        </div>
      </Section>

      {/* ============ Team ============ */}
      <Section
        icon={Users}
        title={t("settings.team.title")}
        sub={t("settings.team.sub")}
        testId="settings-section-team"
        right={team && (
          <div className="text-xs text-ink-muted" data-testid="team-seats">
            {team.seats_limit >= 9999
              ? t("settings.team.unlimited")
              : `${team.seats_used} / ${team.seats_limit} ${t("settings.team.seats")}`}
          </div>
        )}
      >
        {!team ? <Spinner size={20} /> : (
          <>
            {team.seats_limit === 0 && (
              <div className="rounded-lg border border-warn/30 bg-warn/10 px-4 py-3 text-sm text-warn flex items-center gap-2" data-testid="team-upgrade-banner">
                <Crown size={14} /> {t("settings.team.upgrade_required")}
              </div>
            )}

            {team.seats_limit > 0 && (
              <div className="space-y-3">
                <div className="grid md:grid-cols-[1fr_180px_180px_auto] gap-2 items-end">
                  <div>
                    <Label>{t("settings.team.invite_email")}</Label>
                    <Input
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="kollege@firma.at"
                      type="email"
                      disabled={team.seats_remaining <= 0}
                      data-testid="team-invite-email"
                    />
                  </div>
                  <div>
                    <Label>{t("settings.team.invite_role")}</Label>
                    <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} disabled={team.seats_remaining <= 0} data-testid="team-invite-role">
                      {ROLE_KEYS.filter((r) => r !== "owner").map((r) => (
                        <option key={r} value={r}>{ROLES_INFO[r].label_de}</option>
                      ))}
                    </Select>
                    <div className="text-[10px] text-ink-muted mt-1" data-testid="team-invite-role-desc">
                      {ROLES_INFO[inviteRole]?.desc_de}
                    </div>
                  </div>
                  <div>
                    <Label>{t("settings.team.invite_name")}</Label>
                    <Input
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder={t("settings.team.invite_name_ph")}
                      disabled={team.seats_remaining <= 0}
                      data-testid="team-invite-name"
                    />
                  </div>
                  <Button onClick={sendInvite} disabled={inviting || team.seats_remaining <= 0 || !inviteEmail} data-testid="team-invite-submit">
                    {inviting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {t("settings.team.invite_send")}
                  </Button>
                </div>
                {allOutlets.length > 1 && (
                  <div data-testid="team-invite-outlets">
                    <Label>{t("settings.team.assign_outlets", "Outlet-Zuweisung")}</Label>
                    <div className="flex flex-wrap gap-2">
                      {allOutlets.map((o) => {
                        const active = inviteOutletIds.includes(o.id);
                        return (
                          <button
                            key={o.id}
                            type="button"
                            data-testid={`invite-outlet-${o.id}`}
                            onClick={() => setInviteOutletIds((cur) => active ? cur.filter((id) => id !== o.id) : [...cur, o.id])}
                            className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors ${active ? "bg-brand/10 text-brand border-brand/30" : "bg-white/5 text-ink-secondary border-white/10 hover:border-brand/30"}`}
                          >
                            {o.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-ink-muted mt-1">
                      {t("settings.team.assign_outlets_hint", "Leer lassen für alle Outlets")}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Phase 27 — Dev-Token UI removed. Email is sent via Brevo SMTP with console fallback. */}

            <div className="mt-4 bg-bg-void/40 border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="team-members-table">
                <thead className="text-ink-muted text-[10px] uppercase tracking-wider">
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-2.5">{t("settings.team.col_member")}</th>
                    <th className="text-left px-3 py-2.5">{t("settings.team.col_role")}</th>
                    <th className="text-left px-3 py-2.5">{t("settings.team.col_status")}</th>
                    <th className="px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr data-testid="team-owner-row">
                    <td className="px-4 py-2.5">
                      <div className="text-ink-primary font-medium">{team.owner.email}</div>
                      {team.owner.name && <div className="text-[11px] text-ink-muted">{team.owner.name}</div>}
                    </td>
                    <td className="px-3 py-2.5"><Badge tone="brand">{t("settings.team.role_owner")}</Badge></td>
                    <td className="px-3 py-2.5"><Badge tone="ok">{t("settings.team.active")}</Badge></td>
                    <td></td>
                  </tr>
                  {team.members.map((m) => (
                    <tr key={m.id} data-testid={`team-member-${m.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="text-ink-primary truncate max-w-[220px]">{m.email}</div>
                        {m.name && <div className="text-[11px] text-ink-muted truncate max-w-[220px]">{m.name}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <Badge tone={ROLES_INFO[m.team_role]?.tone || "default"} data-testid={`team-member-badge-${m.id}`}>
                              {ROLES_INFO[m.team_role]?.label_de || m.team_role || "Unbekannt"}
                            </Badge>
                          </div>
                          <Select
                            value={m.team_role || "fnb_manager"}
                            onChange={(e) => changeRole(m.id, e.target.value, m.team_outlet_ids || [])}
                            className="text-xs h-7 w-40"
                            data-testid={`team-member-role-${m.id}`}
                          >
                            {ROLE_KEYS.filter((r) => r !== "owner").map((r) => (
                              <option key={r} value={r} title={ROLES_INFO[r].desc_de}>{ROLES_INFO[r].label_de}</option>
                            ))}
                          </Select>
                          {allOutlets.length > 1 && (
                            <div className="flex flex-wrap gap-1 mt-1" data-testid={`team-member-outlets-${m.id}`}>
                              {allOutlets.map((o) => {
                                const ids = m.team_outlet_ids || [];
                                const isAll = ids.length === 0;
                                const active = isAll || ids.includes(o.id);
                                return (
                                  <button
                                    key={o.id}
                                    type="button"
                                    data-testid={`team-member-outlet-toggle-${m.id}-${o.id}`}
                                    onClick={() => toggleMemberOutlet(m, o.id)}
                                    className={`px-1.5 py-0.5 rounded text-[10px] border transition-colors ${active ? "bg-brand/10 text-brand border-brand/30" : "bg-white/5 text-ink-muted border-white/10"}`}
                                    title={o.name}
                                  >
                                    {o.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge tone={m.is_active ? "ok" : "err"}>{m.is_active ? t("settings.team.active") : t("settings.team.disabled")}</Badge>
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <button
                          onClick={() => removeMember(m.id, m.email)}
                          className="text-ink-muted hover:text-err p-1"
                          data-testid={`team-member-remove-${m.id}`}
                          aria-label="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!team.members.length && team.seats_limit > 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-ink-muted">{t("settings.team.no_members")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
